import { useTranslation } from "@refinedev/core";
import Input from "components/input";
import { Label } from "components/label";
import Select from "components/select";
import { SwitchButton } from "components/switchButton";
import Textarea from "components/textarea";
import { TextEditor } from "components/textEditor";
import { IAdditionalSetupForm, ISimpleEventDetail, UserType } from "interfaces";
import { get } from "lodash";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { IS_SHOW_BIB_LIST, LANGUAGES } from "utills/constants";
import { canAccessByType, sanitizeHtml } from "utills/helpers";

export const BasicInformation = ({
  isEdit,
  eventDetailData,
}: {
  isEdit: boolean;
  eventDetailData: ISimpleEventDetail;
}) => {
  const { translate } = useTranslation();
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<IAdditionalSetupForm>(); // retrieve all hook methods
  
  const [activeTab, setActiveTab] = useState("edit");

  return isEdit ? (
    <div className="box-container p-4 sm:p-6">
      <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
        <div className="sm:col-span-2 w-fit">
          <div className="flex flex-row items-center gap-x-4">
            <Controller
              control={control}
              name="basic_information.is_have_participant_list"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton
                  label={translate(
                    "eventSetup.additionalSetup.basicInformation.displayParticipantsList"
                  )}
                  hint={translate(
                    "eventSetup.additionalSetup.basicInformation.displayParticipantsListHint"
                  )}
                  checked={value}
                  onChange={(isChecked) => {
                    onChange(isChecked);
                    if (!isChecked) {
                      setValue("basic_information.show_bib", false);
                    } else {
                      setValue("basic_information.show_bib", true);
                    }
                  }}
                />
              )}
            />
            {watch("basic_information.is_have_participant_list") ? (
              <div>
                <Controller
                  control={control}
                  name="basic_information.show_bib"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key="basic_information.show_bib"
                        inputRef={ref}
                        options={IS_SHOW_BIB_LIST}
                        id="basic_information.show_bib"
                        placeholder="Select Is Show BIB"
                        defaultValue={
                          IS_SHOW_BIB_LIST.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={IS_SHOW_BIB_LIST.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: any }) => {
                          onChange(selected?.value);
                        }}
                        error={get(errors, `basic_information.show_bib`, "")}
                      />
                    );
                  }}
                />
                {get(errors, "basic_information.show_bib", "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `basic_information.show_bib.message`, "")}
                    </span>
                  </p>
                ) : null}
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className="sm:col-span-2 w-fit">
          <Controller
            control={control}
            name="basic_information.is_hide_vat"
            render={({ field: { onChange, value, ref } }) => (
              <SwitchButton
                label={translate(
                  "eventSetup.additionalSetup.basicInformation.hideVAT"
                )}
                hint={translate(
                  "eventSetup.additionalSetup.basicInformation.hideVATHint"
                )}
                checked={value}
                onChange={onChange}
              />
            )}
          />
        </div>
        <div className="sm:col-span-2 w-fit">
          <Controller
            control={control}
            name="basic_information.display_group_button"
            render={({ field: { onChange, value, ref } }) => (
              <SwitchButton
                label={translate(
                  "eventSetup.additionalSetup.basicInformation.displayGroupButton"
                )}
                hint={translate(
                  "eventSetup.additionalSetup.basicInformation.displayGroupButtonHint"
                )}
                checked={value}
                onChange={onChange}
              />
            )}
          />
        </div>
        {canAccessByType(UserType.ADMIN) && (
          <div className="sm:col-span-2 w-fit">
            <Controller
              control={control}
              name="basic_information.has_addon"
              render={({ field: { onChange, value } }) => (
                <SwitchButton
                  label={translate(
                    "eventSetup.additionalSetup.basicInformation.hasAddon"
                  )}
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
            {get(errors, "basic_information.has_addon", "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `basic_information.has_addon.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
        )}
        {canAccessByType(UserType.ADMIN) && (
          <div className="sm:col-span-2 w-fit">
            <Controller
              control={control}
              name="basic_information.use_pass_tool"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton
                  label={translate(
                    "eventSetup.additionalSetup.basicInformation.usePassTool"
                  )}
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
            {get(errors, "basic_information.use_pass_tool", "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `basic_information.use_pass_tool.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
        )}
        {canAccessByType(UserType.ADMIN) && (
          <div className="sm:col-span-2 w-fit">
            <Controller
              control={control}
              name="basic_information.use_authentication"
              render={({ field: { onChange, value, ref } }) => (
                <SwitchButton
                  label="Use Authentication"
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
            {get(errors, "basic_information.use_authentication", "") ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(errors, `basic_information.use_authentication.message`, "")}
                </span>
              </p>
            ) : null}
          </div>
        )}
        <div className={twMerge("sm:col-span-full w-fit", canAccessByType(UserType.ADMIN) ? "sm:col-span-2" : "")}>
          <Controller
            control={control}
            name="basic_information.is_display_on_the_organization_detail_page"
            render={({ field: { onChange, value, ref } }) => (
              <SwitchButton
                hint={translate(
                  "eventSetup.additionalSetup.basicInformation.displayOnTheWhitelabelHint"
                )}
                label={translate(
                  "eventSetup.additionalSetup.basicInformation.displayOnTheWhitelabel"
                )}
                checked={value}
                onChange={onChange}
              />
            )}
          />
          {get(
            errors,
            "basic_information.is_display_on_the_organization_detail_page",
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `basic_information.is_display_on_the_organization_detail_page.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        {canAccessByType(UserType.ADMIN) && (
          <>
            <div className="col-span-full w-fit">
              <Controller
                control={control}
                name="basic_information.is_static_html"
                render={({ field: { onChange, value, ref } }) => (
                  <SwitchButton
                    label="Is Static HTML"
                    checked={value}
                    onChange={onChange}
                  />
                )}
              />
              {get(errors, "basic_information.use_pass_tool", "") ? (
                <p className="error-text">
                  <span className="font-medium">
                    {get(errors, `basic_information.use_pass_tool.message`, "")}
                  </span>
                </p>
              ) : null}
            </div>
            {watch("basic_information.is_static_html") && (
              <div className="col-span-full">
                <Controller
                  control={control}
                  name="basic_information.static_html_content"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <div className="flex flex-col gap-0">
                        {/* Tab Header */}
                        <div className="flex border border-b-0 border-gray-200 rounded-t-md overflow-hidden w-fit">
                          <button
                            type="button"
                            onClick={() => setActiveTab("edit")}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${
                              activeTab === "edit"
                                ? "bg-white text-gray-900 border-b-2 border-b-blue-500"
                                : "bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => setActiveTab("preview")}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${
                              activeTab === "preview"
                                ? "bg-white text-gray-900 border-b-2 border-b-blue-500"
                                : "bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            Preview
                          </button>
                        </div>

                        {/* Tab Content */}
                        <div className="border border-gray-200 rounded-b-md rounded-tr-md">
                          {activeTab === "edit" ? (
                            <Textarea
                              id="basic_information.static_html_content"
                              placeholder="Enter static HTML content"
                              error={get(errors, `basic_information.static_html_content`, "")}
                              onChange={(e: { target: { value: string; }; }) => onChange(sanitizeHtml(e.target.value))}
                              value={value}
                              className="rounded-none rounded-b-md border-0 min-h-[320px]"
                            />
                          ) : (
                            <iframe
                              srcDoc={value || "<p style='color:#9ca3af;font-size:14px;padding:12px'>Nothing to preview</p>"}
                              className="w-full min-h-[400px] border-0 rounded-b-md rounded-tr-md"
                              title="HTML Preview"
                            />
                          )}
                        </div>
                      </div>
                    );
                  }}
                />
                {errors["basic_information"]?.static_html_content && (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, `basic_information.static_html_content.message`, "")}
                    </span>
                  </p>
                )}
              </div>
            )}
          </>
        )}

        <div className="col-span-full">
          <Label
            id="basic_information.event_term_url"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.eventTClink"
            )}
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.eventTClinkHint"
            )}
            required
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map((lang) => (
              <div key={lang}>
                <div
                  className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                >
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="w-4 shrink-0"
                  />
                  <p>
                    {
                      LANGUAGES.find((language) => language.value === lang)
                        ?.label
                    }
                  </p>
                </div>
                <Controller
                  name={`basic_information.event_term_url.${lang}`}
                  control={control}
                  render={({ field: { onChange, value = "" } }) => (
                    <TextEditor
                      key={`basic_information.event_term_url.${lang}`}
                      placeholder={`${translate(
                        "eventSetup.additionalSetup.basicInformation.eventTClink"
                      )} (${lang})`}
                      value={value ?? ""}
                      onChange={onChange}
                      maxHeight={360}
                    />
                  )}
                />
                {get(errors, `basic_information.event_term_url.${lang}`, "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(
                        errors,
                        `basic_information.event_term_url.${lang}.message`,
                        ""
                      )}
                    </span>
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
        <div className="sm:col-span-3">
          <Label
            id="basic_information.hold_ticket_time"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.holdTicketTime"
            )}
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.holdTicketTimeHint"
            )}
            required
          />
          <Input
            id={`basic_information.hold_ticket_time`}
            type="number"
            error={get(errors, `basic_information.hold_ticket_time`, "")}
            placeholder={translate(
              "eventSetup.additionalSetup.basicInformation.holdTicketTime"
            )}
            {...register("basic_information.hold_ticket_time", {
              valueAsNumber: true,
            })}
            min={5}
          />
          {get(errors, `basic_information.hold_ticket_time`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(errors, `basic_information.hold_ticket_time.message`, "")}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="basic_information.max_tickets_to_buy"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.maxTicketsQuantityPerAccount"
            )}
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.maxTicketsQuantityPerAccountHint"
            )}
          />
          <Input
            id={`basic_information.max_tickets_to_buy`}
            type="number"
            disabled={canAccessByType(UserType.ADMIN) && !watch("basic_information.use_authentication")}
            error={get(errors, `basic_information.max_tickets_to_buy`, "")}
            placeholder={translate(
              "eventSetup.additionalSetup.basicInformation.maxTicketsQuantityPerAccount"
            )}
            {...register("basic_information.max_tickets_to_buy", {
              valueAsNumber: true,
            })}
            min={0}
          />
          {get(errors, `basic_information.max_tickets_to_buy`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `basic_information.max_tickets_to_buy.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="basic_information.order_min_qty_ticket"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.minTicketsQuantityPerOrder"
            )}
            required
          />
          <Input
            id={`basic_information.order_min_qty_ticket`}
            type="number"
            error={get(errors, `basic_information.order_min_qty_ticket`, "")}
            placeholder={translate(
              "eventSetup.additionalSetup.basicInformation.minTicketsQuantityPerOrder"
            )}
            min={1}
            {...register("basic_information.order_min_qty_ticket", {
              valueAsNumber: true,
            })}
          />
          {get(errors, `basic_information.order_min_qty_ticket`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `basic_information.order_min_qty_ticket.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="basic_information.order_max_qty_ticket"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.maxTicketsQuantityPerOrder"
            )}
            required
          />
          <Input
            id={`basic_information.order_max_qty_ticket`}
            type="number"
            error={get(errors, `basic_information.order_max_qty_ticket`, "")}
            placeholder={translate(
              "eventSetup.additionalSetup.basicInformation.maxTicketsQuantityPerOrder"
            )}
            min={1}
            {...register("basic_information.order_max_qty_ticket", {
              valueAsNumber: true,
            })}
          />
          {get(errors, `basic_information.order_max_qty_ticket`, "") ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `basic_information.order_max_qty_ticket.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>

        <div className="col-span-full">
          <Label
            id="basic_information.note_individual_ticket_purchase_flow"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.noteInTheIndividualFlow"
            )}
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.noteInTheIndividualFlowHint"
            )}
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map((lang) => (
              <div key={lang}>
                <div
                  className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                >
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="w-4 shrink-0"
                  />
                  <p>
                    {
                      LANGUAGES.find((language) => language.value === lang)
                        ?.label
                    }
                  </p>
                </div>
                <Controller
                  control={control}
                  name={`basic_information.note_individual_ticket_purchase_flow.${lang}`}
                  render={({ field: { onChange, value = "" } }) => (
                    <TextEditor
                      key={`basic_information.note_individual_ticket_purchase_flow.${lang}`}
                      placeholder={translate(
                        "eventSetup.additionalSetup.basicInformation.noteInTheIndividualFlow"
                      )}
                      value={value ?? ""}
                      onChange={onChange}
                    />
                  )}
                />

                {get(
                  errors,
                  `basic_information.note_individual_ticket_purchase_flow.${lang}`,
                  ""
                ) ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(
                        errors,
                        `basic_information.note_individual_ticket_purchase_flow.${lang}.message`,
                        ""
                      )}
                    </span>
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-full">
          <Label
            id="basic_information.note_group_ticket_purchase_flow"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.noteInTheGroupFlow"
            )}
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.noteInTheGroupFlowHint"
            )}
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map((lang) => (
              <div key={lang}>
                <div
                  className={`w-fit flex flex-row items-center gap-x-1 p-2 -mb-1 rounded-tl-md rounded-tr-md border-magnolia-600 border-solid border-[1px] bg-magnolia-400 font-medium text-sm`}
                >
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="w-4 shrink-0"
                  />
                  <p>
                    {
                      LANGUAGES.find((language) => language.value === lang)
                        ?.label
                    }
                  </p>
                </div>
                <Controller
                  control={control}
                  name={`basic_information.note_group_ticket_purchase_flow.${lang}`}
                  render={({ field: { onChange, value = "" } }) => (
                    <TextEditor
                      key={`basic_information.note_group_ticket_purchase_flow.${lang}`}
                      value={value ?? ""}
                      placeholder={translate(
                        "eventSetup.additionalSetup.basicInformation.noteInTheGroupFlow"
                      )}
                      onChange={onChange}
                    />
                  )}
                />

                {get(
                  errors,
                  `basic_information.note_group_ticket_purchase_flow.${lang}`,
                  ""
                ) ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(
                        errors,
                        `basic_information.note_group_ticket_purchase_flow.${lang}.message`,
                        ""
                      )}
                    </span>
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};
